import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useSubscription } from "@/context/SubscriptionContext";

const TONES = {
  trial:    { bg: "#EFF6FF", border: "#2563EB", text: "#1E40AF" },
  past_due: { bg: "#FFFBEB", border: "#F59E0B", text: "#92400E" },
  failed:   { bg: "#FFF1F2", border: "#E11D48", text: "#9F1239" },
  cancel:   { bg: "#FFF7ED", border: "#F97316", text: "#9A3412" },
  blocked:  { bg: "#FEF2F2", border: "#DC2626", text: "#991B1B" },
  renewal:  { bg: "#FFFBEB", border: "#F59E0B", text: "#92400E" },
};

/**
 * Workspace-level subscription banner. Renders inside Layout above page content
 * whenever the subscription needs attention (trial, past_due, suspended, ...).
 */
export default function SubscriptionBanner() {
  const sub = useSubscription();
  const { user } = useAuth() || {};
  // 2026-08-25 · Renewal warning derived from the login-time
  // `subscription_validity.expiring_soon` flag (30-day window before
  // valid_to). Overrides other advisory banners because renewal
  // action is the most urgent one during this window.
  const validity = user?.subscription_validity;
  if (validity?.expiring_soon) {
    const days = validity.days_to_expiry;
    const label = days === 0 ? "today"
      : days === 1 ? "in 1 day"
      : `in ${days} days`;
    const cfg = {
      tone: TONES.renewal,
      title: `License renewal due ${label}`,
      message: user?.license_number
        ? `License ${user.license_number} · Contact CertOpz to renew before ${validity.valid_to ? String(validity.valid_to).slice(0, 10) : "expiry"} to avoid interruption.`
        : "Contact CertOpz to renew before expiry to avoid interruption.",
      cta: sub?.canBilling ? { label: "Manage billing", to: "/subscription/billing-status" } : null,
    };
    return (
      <div className="mb-4 rounded-xl px-4 py-3 flex items-center gap-3"
           style={{ background: cfg.tone.bg, border: `1px solid ${cfg.tone.border}` }}
           data-testid="subscription-renewal-banner">
        <div className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-white text-sm"
             style={{ background: cfg.tone.border }}>!</div>
        <div className="flex-1">
          <div className="font-bold" style={{ color: cfg.tone.text }}>{cfg.title}</div>
          <div className="text-xs" style={{ color: cfg.tone.text, opacity: 0.85 }}>{cfg.message}</div>
        </div>
        {cfg.cta ? (
          <Link to={cfg.cta.to} className="text-xs font-semibold px-3 py-1.5 rounded-md text-white"
                style={{ background: cfg.tone.border }} data-testid="subscription-renewal-cta">
            {cfg.cta.label} →
          </Link>
        ) : null}
      </div>
    );
  }
  if (!sub || sub.loading || !sub.entitlements) return null;

  const status = sub.status;
  const days = sub.daysRemaining;
  const canBilling = sub.canBilling;

  let cfg = null;

  if (status === "TRIALING") {
    cfg = {
      tone: TONES.trial,
      title: `You are on Trial · ${days ?? 0} days remaining`,
      message: "Upgrade to keep access to all your data and features after the trial ends.",
      cta: canBilling ? { label: "View plans", to: "/subscription/change-plan" } : null,
    };
  } else if (status === "PAST_DUE") {
    cfg = {
      tone: TONES.past_due,
      title: "Payment failed",
      message: "Please update billing to avoid service interruption.",
      cta: canBilling ? { label: "Update billing", to: "/subscription/billing-status" } : null,
    };
  } else if (status === "PAYMENT_FAILED") {
    cfg = {
      tone: TONES.failed,
      title: "Subscription restricted — payment failed",
      message: canBilling ? "Resolve the payment issue to restore access." : "Ask your workspace Owner/Admin to update payment.",
      cta: canBilling ? { label: "Open billing", to: "/subscription/billing-status" } : null,
    };
  } else if (status === "SUSPENDED") {
    cfg = {
      tone: TONES.blocked,
      title: "Your workspace is suspended",
      message: canBilling ? "Update payment to restore access." : "Ask your workspace Owner/Admin to update payment.",
      cta: canBilling ? { label: "Restore access", to: "/subscription/billing-status" } : null,
    };
  } else if (status === "CANCELLED") {
    cfg = {
      tone: TONES.cancel,
      title: `Subscription cancelled · ${days ?? 0} days remaining`,
      message: "Reactivate to keep your workspace beyond the current period.",
      cta: canBilling ? { label: "Reactivate", to: "/subscription/billing-status" } : null,
    };
  } else if (status === "EXPIRED" || status === "NONE") {
    cfg = {
      tone: TONES.blocked,
      title: "Choose a plan to continue",
      message: canBilling ? "Your subscription has ended. Choose a new plan." : "Ask your workspace Owner/Admin to choose a plan.",
      cta: canBilling ? { label: "Choose plan", to: "/subscription/change-plan" } : null,
    };
  }

  if (!cfg) return null;
  const { tone, title, message, cta } = cfg;

  return (
    // 2026-08-25 · Make the banner self-contained so no operator ever
    // has to squint to read it: (a) `items-start` + `flex-wrap` so on
    // narrow viewports the message wraps freely instead of colliding
    // with the CTA; (b) the CTA gets `shrink-0 whitespace-nowrap` so
    // "View plans →" is never truncated; (c) message text bumped from
    // 12px → 13px (`text-[13px] leading-snug`) for readability; (d)
    // slightly taller padding (`py-3.5`). The `mb-4` bottom margin is
    // preserved so the content below still gets pushed down by the
    // banner's true height — the flow is native, no overlap possible.
    <div
      className="mb-4 rounded-xl px-4 py-3.5 flex items-start gap-3 flex-wrap sm:flex-nowrap"
      style={{ background: tone.bg, border: `1px solid ${tone.border}` }}
      data-testid="subscription-banner"
    >
      <div
        className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-white text-sm shrink-0 mt-0.5"
        style={{ background: tone.border }}
      >!</div>
      <div className="flex-1 min-w-0">
        <div className="font-bold leading-tight" style={{ color: tone.text }}>{title}</div>
        <div className="text-[13px] leading-snug mt-0.5" style={{ color: tone.text, opacity: 0.9 }}>{message}</div>
      </div>
      {cta ? (
        <Link
          to={cta.to}
          className="text-xs font-semibold px-3 py-1.5 rounded-md text-white shrink-0 whitespace-nowrap self-center"
          style={{ background: tone.border }}
          data-testid="subscription-banner-cta"
        >
          {cta.label} →
        </Link>
      ) : null}
    </div>
  );
}
